import { Loadable } from '@/components/loaders';
import { Button } from '@/lib';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PerformanceAudienceDashboardEmpty, PerformanceAudienceFilters, PerformanceAudiencePageHeader } from './components';
import { usePerformanceAudienceBootstrap } from './hooks/usePerformanceAudienceBootstrap';
import { PerformanceDashboardContainer } from './PerformanceDashboardContainer';
import ErrorBoundary from '@/components/ErrorBoundary';

export const PerformanceAudiencePage: FC = () => {
  const { t } = useTranslation('performanceAudience');

  const {
    filters,
    filterOptions,
    setAudienceFilter,
    setGoogleScopeFilter,
    setMetaScopeIds,
    setDateRangeFilter,
    setCustomDateRange,
    isInitialLoading,
    isFiltersReady,
    hasAudiences,
    hasSources,
    isError,
    refetch,
  } = usePerformanceAudienceBootstrap();

  const handleExport = useCallback(() => {
    // Placeholder for dashboard export
  }, []);

  const canLoadDashboard = hasAudiences && hasSources;

  return (
    <ErrorBoundary>
      <div className='grid w-full grid-cols-1 gap-[18px] pt-7'>
        <PerformanceAudiencePageHeader onExport={handleExport} syncLabel={t('page.syncPlaceholder')} />
        <ErrorBoundary>
          {isError ? (
            <div className='rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-800'>
              <p>{t('errors.loadFailed')}</p>
              <Button intent='secondary' size='sm' className='mt-4' onClick={() => refetch()}>
                {t('errors.retry')}
              </Button>
            </div>
          ) : (
            <Loadable isLoading={isInitialLoading}>
              <div className='flex flex-col gap-[18px]'>
                <PerformanceAudienceFilters
                  audiences={filterOptions.audiences}
                  googleScopes={filterOptions.googleScopes}
                  metaScopes={filterOptions.metaScopes}
                  dateRanges={filterOptions.dateRanges}
                  selectedAudienceId={filters.audienceId}
                  selectedGoogleScopeId={filters.googleScopeId}
                  selectedMetaScopeIds={filters.metaScopeIds}
                  selectedDateRangeId={filters.dateRange}
                  selectedDateFrom={filters.dateFrom}
                  selectedDateTo={filters.dateTo}
                  onAudienceChange={setAudienceFilter}
                  onGoogleScopeChange={setGoogleScopeFilter}
                  onMetaScopeIdsChange={setMetaScopeIds}
                  onDateRangeChange={setDateRangeFilter}
                  onCustomDateRangeApply={setCustomDateRange}
                />
                <ErrorBoundary>
                  {canLoadDashboard ? (
                    <PerformanceDashboardContainer
                      filters={filters}
                      filterOptions={{
                        audiences: filterOptions.audiences,
                        googleScopes: filterOptions.googleScopes,
                        metaScopes: filterOptions.metaScopes,
                      }}
                      enabled={isFiltersReady}
                    />
                  ) : (
                    <PerformanceAudienceDashboardEmpty />
                  )}
                </ErrorBoundary>
              </div>
            </Loadable>
          )}
        </ErrorBoundary>
      </div>
    </ErrorBoundary>
  );
};
